import { METRO_STATIONS } from "./metroStations";

// Haversine distance in metres between two lat/lng points.
function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in metres
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Typical average walking pace = 80 m/min (~4.8 km/h). Round up so the badge
// never over-promises. Cap the "walk" claim at 25 min; beyond that, we don't
// pretend the metro is walkable.
const WALK_METRES_PER_MIN = 80;
const WALK_MAX_MIN = 25;

/**
 * Return the nearest operational metro station to a given point, or null if
 * no station is within a reasonable walk (WALK_MAX_MIN * WALK_METRES_PER_MIN
 * metres ≈ 2 km).
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {{name: string, line: 'red'|'green', distance_m: number, minutes: number} | null}
 */
export function nearestMetro(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number" || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }
  let best = null;
  for (const s of METRO_STATIONS) {
    // Skip stations that don't exist yet (Blue Line opens Sep 2029) —
    // a future station can't offer a walkable commute today.
    if (s.future) continue;
    const d = haversineMeters(lat, lng, s.lat, s.lng);
    if (!best || d < best.distance_m) {
      best = { name: s.name, line: s.line, distance_m: d };
    }
  }
  if (!best) return null;
  const minutes = Math.max(1, Math.ceil(best.distance_m / WALK_METRES_PER_MIN));
  if (minutes > WALK_MAX_MIN) return null;
  return { ...best, minutes };
}

// Short, humane label — never claims "0 min".
export function metroBadgeLabel(m) {
  if (!m) return null;
  return `${m.minutes} min walk · ${m.name}`;
}

// Semantic colour helper — matches the Metro overlay palette on /map.
export function metroBadgeAccent(line) {
  if (line === "red") return "#E30613";
  if (line === "green") return "#009845";
  return "#001588";
}
